import {
  TQueryStringFeedFilter,
  TQueryStringFilterSettings,
  TQueryStringLanguage,
  TQueryStringLanguageTypeNames,
  TQueryStringNarrative,
  TQueryStringNarrativeItem,
  clearSecondaryQueryStringFeedFilter,
  clearQueryStringFeedFilter,
  getFiltersFromQueryString,
  setQueryStringFeedFilter,
  setQueryStringKeywordFilter,
  setQueryStringLanguageFilter,
  setQueryStringMediaCategory,
  setQueryStringNarrativeFilter,
  setQueryStringPlatformsFilter,
  setQueryStringRelevance,
  SortFields,
  setQueryStringSort,
  setQueryStringSortDirection,
  setQueryStringCondenseText,
  setQueryStringLocationFilter,
  clearQueryStringLocationFilter,
  setQueryStringDateReferenceFilter,
  clearQueryStringDateReferenceFilter,
} from "#next/components/explorer/base/explorerQueryStrings";
import {
  IMediaSearchResult,
  SearchResult,
  GraphMode,
} from "#next/utils/interfaces";
import { TExplorerHighlightText, getS3ImageURL } from "#next/utils/shared";
import { RouterOutput } from "#next/utils/trpc";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { groupBy, uniqBy } from "lodash";
import { TFeedFilterByQueryStringNames } from "#next/components/explorer/base/explorerQueryStrings";
import {
  RankingData,
  generateRankings,
} from "#next/utils/explorer/generateRankings";
import { SummaryViewerSeriesData } from "#next/utils/explorer/shared";
import { generateGraphData } from "#next/utils/explorer/generateGraphs";
import { toYYYYMMDD } from "#next/utils/explorer/shared";

export type Narrative =
  RouterOutput["savedSearch"]["getById"]["SavedSearchNarratives"][0];

export type EvaluatedNarrative = {
  postId: string;
  narratives: {
    id: string;
    narrative: string;
    similarity: number;
  }[];
};

export type TTabNames = Exclude<TFeedFilterByQueryStringNames, "language">;

export enum GraphType {
  spline = "spline",
  column = "column",
}

export interface ExplorerUIState {
  originalSearchResults: SearchResult[];
  originalSearchResultsHash: string;
  filteredSearchResults: SearchResult[];
  feedSearchResults: SearchResult[];
  textHighlights: TExplorerHighlightText[];
  platforms: string[];
  narratives: Narrative[];
  evaluatedNarratives: EvaluatedNarrative[] | undefined;
  reportName: string | undefined;
  keywordFilterHighlight:
    | [{ type: "user keyword filter"; texts: string[] }]
    | null;
  queryStringFilters: TQueryStringFilterSettings;
  mediaCategoryResultsByLabel: Record<
    string,
    Record<string, IMediaSearchResult>
  >;
  isLoaded: boolean; // We load some initial default results when loading, so the TopControlBar can be displayed. During this time, isLoaded=false.
  filterStateFakeUrl: string | null;
  blurMedia: boolean;
  translateAll: boolean;
  searchType?: string;
  graphData: SummaryViewerSeriesData[];
  graphMode: GraphMode;
  graphType: GraphType;
  rankingsData: RankingData[];
  activeRankingsTab: TTabNames;
}

const initialExplorerUiState = (
  searchResults: SearchResult[],
  searchResultsHash: string,
  textHighlights: TExplorerHighlightText[],
  reportName: string | undefined,
  narratives: Narrative[],
  isLoaded: boolean,
  filterStateFakeUrl: string | null,
  blurMedia: boolean,
  searchType?: string,
): ExplorerUIState => {
  const rankingsData = generateRankings(searchResults, "platform");
  const graphData = generateGraphData(
    searchResults,
    rankingsData,
    "platform",
    GraphMode.platform,
    GraphType.spline,
    null,
  );
  return {
    originalSearchResults: searchResults,
    originalSearchResultsHash: searchResultsHash,
    filteredSearchResults: searchResults,
    feedSearchResults: searchResults,
    textHighlights,
    platforms: [
      ...new Set(searchResults.map((result) => result.platform)),
    ].sort(),
    reportName,
    narratives,
    evaluatedNarratives: undefined,
    keywordFilterHighlight: null,
    queryStringFilters: getFiltersFromQueryString(filterStateFakeUrl),
    mediaCategoryResultsByLabel: {},
    isLoaded,
    filterStateFakeUrl,
    blurMedia,
    translateAll: false,
    searchType: searchType,
    activeRankingsTab: "platform",
    rankingsData: rankingsData,
    graphMode: GraphMode.platform,
    graphType: GraphType.spline,
    graphData: graphData,
  };
};

type ExplorerState = Record<string, ExplorerUIState>;

const initialState = (): ExplorerState => ({});

const getUiStateOrThrow = (state: ExplorerState, searchResultsKey: string) => {
  const uiState = state[searchResultsKey];
  if (!uiState) {
    throw new Error(`No state found for key ${searchResultsKey}`);
  }
  return uiState;
};

const _filterByKeyword = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const keywords = queryFilters.keywords?.map((kw) => kw.toLowerCase());
  if (keywords === undefined || keywords.length === 0) {
    return searchResults;
  }
  return searchResults.filter((result) =>
    keywords.every((kw) => result.text.toLowerCase().includes(kw)),
  );
};

const _filterByDateReference = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const dateReference = queryFilters.dateReference;
  if (!dateReference) {
    return searchResults;
  }
  const res = searchResults.filter(
    (result) =>
      result.dates &&
      result.dates
        .map((date) => toYYYYMMDD(new Date(date)))
        .includes(dateReference),
  );
  return res;
};

const _condenseText = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  if (!queryFilters.condenseText) return searchResults;
  const searchResultGroups = groupBy(searchResults, "text");
  const uniqueSearchResults = uniqBy(searchResults, "text");
  const uniqueSearchResultsCounted = uniqueSearchResults.map((result) => {
    const count = searchResultGroups[result.text]?.length;
    return { ...result, occurrences: count };
  });
  return uniqueSearchResultsCounted;
};

const _filterByPlatform = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const platforms = queryFilters.platforms;
  if (platforms === null) {
    return searchResults;
  }
  const platformSet = new Set(platforms);
  return searchResults.filter((result) => platformSet.has(result.platform));
};

const _checkNarrativeMatch = (
  evaluatedNarrative: EvaluatedNarrative,
  narrative: TQueryStringNarrativeItem,
  accumulator: boolean[],
) => {
  if (narrative.score === 0) {
    throw new Error("Shouldn't be possible to have a narrative set to 0");
  }
  const evaluated = evaluatedNarrative.narratives.find(
    (narr) => narr.id === narrative.id,
  );
  if (!evaluated) {
    throw new Error("Should find a similarity score for every narrative");
  }
  accumulator.push(evaluated.similarity >= narrative.score);
};

const _filterByNarrative = (
  evaluatedNarratives: EvaluatedNarrative[] | undefined,
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const narrativeFilters = queryFilters.narrative;
  if (!narrativeFilters || !evaluatedNarratives) {
    return searchResults;
  }

  const scoredBySearchResultId = Object.fromEntries(
    evaluatedNarratives.map((narrative) => [narrative.postId, narrative]),
  );

  const [n1, n2, n3] = narrativeFilters;

  return searchResults.filter((result) => {
    const evaluated = scoredBySearchResultId[result.id];
    if (!evaluated) {
      throw new Error(
        "Should have found evaluated narrative for search result",
      );
    }
    const matches: boolean[] = [];
    _checkNarrativeMatch(evaluated, n1, matches);
    if (n2) {
      _checkNarrativeMatch(evaluated, n2, matches);
    }
    if (n3) {
      _checkNarrativeMatch(evaluated, n3, matches);
    }
    return matches.every((match) => match);
  });
};

const _filterByRelevance = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const relevance = queryFilters.relevance;
  if (relevance === null || relevance === 0) {
    return searchResults;
  }
  return searchResults.filter(
    (result) => result.normalized_relevance >= relevance,
  );
};

const _filterByLocation = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const latitude = queryFilters.latitude;
  const longitude = queryFilters.longitude;
  if (latitude === null || longitude === null) {
    return searchResults;
  }
  return searchResults.filter(
    (result) =>
      result.locations &&
      result.locations.filter(
        (location) => location.lat === latitude && location.lon === longitude,
      ).length > 0,
  );
};

const _filterFeed = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const feedFilters = queryFilters.feed;
  if (feedFilters === null) {
    return searchResults;
  }
  const { filterBy, value, date } = feedFilters;
  let filtered = searchResults;
  if (date) {
    filtered = filtered.filter((result) => {
      const dateInUserTz = DateTime.fromISO(result.timestamp)
        .toLocal()
        .toFormat("yyyy-MM-dd");
      return dateInUserTz === date;
    });
  }
  switch (filterBy) {
    case "platform":
      return filtered.filter((result) => result.platform === value);
    case "hashtag": {
      return filtered.filter((result) => result.hashtags?.includes(value));
    }
    case "link": {
      return filtered.filter((result) => result.links?.includes(value));
    }
    case "username": {
      return filtered.filter((result) => result.username === value);
    }
    case "entity": {
      return filtered.filter((result) => result.entities?.includes(value));
    }
    case "language":
      return filtered.filter((result) => {
        const score = result[value as TQueryStringLanguageTypeNames];
        return score >= 0.7;
      });
    default:
      const _never: never = filterBy;
      return _never;
  }
};

const _filterByLanguage = (
  queryFilters: TQueryStringFilterSettings,
  searchResults: SearchResult[],
) => {
  const setting = queryFilters.language;

  const filters: ((result: SearchResult) => boolean)[] = [];

  // If set to `0` or `null` then the filter is off and
  // shouldn't be considered. `0` and `null` are distinguished because
  // the original Explorer UI didn't treat them as the same thing. If we continue
  // treating both as "off" then we could merge those states and just have one.
  const maybeAddFilter = (key: TQueryStringLanguageTypeNames) => {
    const score = setting[key];
    if (score !== null && score > 0) {
      filters.push((result) => result[key] >= score);
    }
  };

  maybeAddFilter("hateful");
  maybeAddFilter("negative");
  maybeAddFilter("violent");
  maybeAddFilter("offensive");

  if (filters.length === 0) {
    return searchResults;
  }

  const filterResult =
    setting.mode === "and"
      ? (searchResult: SearchResult) =>
          filters.every((filter) => filter(searchResult))
      : (searchResult: SearchResult) =>
          filters.some((filter) => filter(searchResult));

  return searchResults.filter((searchResult) => filterResult(searchResult));
};

function _filterByMediaCategory(
  queryFilters: TQueryStringFilterSettings,
  mediaCategoryResultsByLabel: ExplorerUIState["mediaCategoryResultsByLabel"],
  searchResults: SearchResult[],
) {
  const label = queryFilters.mediaCategory;
  if (label === null) {
    return searchResults;
  }
  const mediaResults = mediaCategoryResultsByLabel[label];
  if (!mediaResults) {
    return searchResults;
  }
  const filtered: SearchResult[] = [];
  searchResults.forEach((searchResult) => {
    const mediaResult = mediaResults[searchResult.id];
    if (!mediaResult) {
      return;
    }
    const { s3Key, s3BucketName } = mediaResult;
    searchResult.media = [getS3ImageURL(s3BucketName, s3Key)];
    filtered.push(searchResult);
  });
  return filtered.sort((a, b) => {
    const aKnnScore = mediaResults[a.id]?.knnScore || 0;
    const bKnnScore = mediaResults[b.id]?.knnScore || 0;
    return bKnnScore - aKnnScore;
  });
}

const updateFilteredSearchResults = (
  state: ExplorerState,
  searchResultsKey: string,
) => {
  const uiState = getUiStateOrThrow(state, searchResultsKey);

  const queryFilters = getFiltersFromQueryString(uiState.filterStateFakeUrl);

  let filteredSearchResults = uiState.originalSearchResults;

  filteredSearchResults = _filterByPlatform(
    queryFilters,
    filteredSearchResults,
  );
  filteredSearchResults = _filterByLanguage(
    queryFilters,
    filteredSearchResults,
  );
  filteredSearchResults = _filterByRelevance(
    queryFilters,
    filteredSearchResults,
  );
  filteredSearchResults = _filterByKeyword(queryFilters, filteredSearchResults);
  filteredSearchResults = _filterByNarrative(
    uiState.evaluatedNarratives,
    queryFilters,
    filteredSearchResults,
  );

  filteredSearchResults = _filterByMediaCategory(
    queryFilters,
    uiState.mediaCategoryResultsByLabel,
    filteredSearchResults,
  );

  filteredSearchResults = _filterByLocation(
    queryFilters,
    filteredSearchResults,
  );

  filteredSearchResults = _filterByDateReference(
    queryFilters,
    filteredSearchResults,
  );

  function sort(a: SearchResult, b: SearchResult) {
    const left = queryFilters.sortDirection === "desc" ? a : b;
    const right = queryFilters.sortDirection === "desc" ? b : a;
    if (!queryFilters.sort || queryFilters.sort === "Date") {
      return left.timestamp.localeCompare(right.timestamp);
    } else if (queryFilters.sort === "Username") {
      // the conditional statements are required to move null usernames to the bottom when sorting
      if (left.username === right.username) {
        return 0;
      }
      if (left.username === null) {
        return -1;
      }
      if (right.username === null) {
        return 1;
      }
      return (right.username ?? "").localeCompare(left.username ?? "");
    } else if (queryFilters.sort === "Relevance") {
      return left.normalized_relevance - right.normalized_relevance;
    } else if (queryFilters.sort === "Violent") {
      return left.violent - right.violent;
    } else if (queryFilters.sort === "Hateful") {
      return left.hateful - right.hateful;
    } else if (queryFilters.sort === "Negative") {
      return left.negative - right.negative;
    } else if (queryFilters.sort === "Offensive") {
      return left.offensive - right.offensive;
    } else if (queryFilters.sort === "Engagement") {
      return (left.engagement_rate ?? 0) - (right.engagement_rate ?? 0);
    } else if (queryFilters.sort === "Occurrences") {
      return (left.occurrences ?? 1) - (right.occurrences ?? 1);
    }
    throw new Error(`Impossible sort ${queryFilters.sort}`);
  }

  filteredSearchResults = _condenseText(queryFilters, filteredSearchResults);

  uiState.feedSearchResults = _filterFeed(
    queryFilters,
    filteredSearchResults,
  ).toSorted(sort);

  uiState.filteredSearchResults = filteredSearchResults.toSorted(sort);

  uiState.rankingsData = generateRankings(
    filteredSearchResults,
    uiState.activeRankingsTab,
  );

  uiState.graphData = generateGraphData(
    filteredSearchResults,
    uiState.rankingsData,
    uiState.activeRankingsTab,
    uiState.graphMode,
    uiState.graphType,
    uiState.queryStringFilters.feed
      ? uiState.queryStringFilters.feed.value
      : null,
  );
};

function updateQueryStringFilters(
  state: ExplorerState,
  searchResultsKey: string,
) {
  const uiState = getUiStateOrThrow(state, searchResultsKey);
  uiState.queryStringFilters = getFiltersFromQueryString(
    uiState.filterStateFakeUrl,
  );
}

/**
 The query string may include narrative IDs that no longer exist.
 Update the query string so that it only contains valid narrative IDs, or remove narrative filtering
 from the query string if no narrative IDs from the query string still exist.
 */
function syncFilteredNarratives(
  narratives: Narrative[],
  filterStateFakeUrl: string | null,
) {
  const narrativeQueryString =
    getFiltersFromQueryString(filterStateFakeUrl).narrative;
  // If no narratives are in the query string there's nothing to do
  if (!narrativeQueryString) {
    return;
  }

  const currentNarrativeIds = new Set(narratives.map((narr) => narr.id));
  const narrativesThatStillExist = narrativeQueryString.filter(
    (item) => item && currentNarrativeIds.has(item.id),
  );
  // If none of the narratives in the query string still exist then clear the narrative filter
  if (narrativesThatStillExist.length === 0) {
    setQueryStringNarrativeFilter(null, filterStateFakeUrl);
    return;
  }
  // If all of the narratives in the query string still exist then there is nothing to do
  if (narrativesThatStillExist.length === currentNarrativeIds.size) {
    return;
  }

  const [narrative1, narrative2, narrative3] = narrativesThatStillExist;
  if (!narrative1) {
    throw new Error(
      "Shouldn't be possible to get here. We should have found at least one narrative in the query string that also exists in `narratives`",
    );
  }
  setQueryStringNarrativeFilter(
    [narrative1, narrative2 ?? null, narrative3 ?? null],
    filterStateFakeUrl,
  );
}

const explorerSlice = createSlice({
  name: "explorer",
  initialState,
  reducers: {
    setEvaluatedNarratives: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        evaluatedNarratives: EvaluatedNarrative[];
      }>,
    ) => {
      const _state = state[action.payload.searchResultsKey];
      if (!_state) {
        throw new Error(
          `Should have found state for key ${action.payload.searchResultsKey}`,
        );
      }
      _state.evaluatedNarratives = action.payload.evaluatedNarratives;
    },
    setPlatformFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        platforms: string[] | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringPlatformsFilter(
        action.payload.platforms,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setLanguageFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        language: TQueryStringLanguage;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringLanguageFilter(
        action.payload.language,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setNarrativeFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        narratives: TQueryStringNarrative | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringNarrativeFilter(
        action.payload.narratives,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setKeywordFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        keywords: string[] | null;
      }>,
    ) => {
      const { searchResultsKey, keywords } = action.payload;
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringKeywordFilter(
        keywords,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, searchResultsKey);
      if (keywords && keywords?.length > 0) {
        uiState.keywordFilterHighlight = [
          {
            type: "user keyword filter",
            texts: keywords,
          },
        ];
      } else {
        uiState.keywordFilterHighlight = null;
      }
    },
    setSort: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        sort: (typeof SortFields)[number] | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringSort(
        action.payload.sort,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setSortDirection: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        sortDirection: "asc" | "desc" | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringSortDirection(
        action.payload.sortDirection,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setMediaCategoryFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        label: string | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringMediaCategory(
        action.payload.label,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setMediaCategoryFilterResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        label: string;
        results: ExplorerUIState["mediaCategoryResultsByLabel"][string];
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.mediaCategoryResultsByLabel[action.payload.label] =
        action.payload.results;
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setRelevanceFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        relevance: number | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringRelevance(
        action.payload.relevance,
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setLocationFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        latitude: number | null;
        longitude: number | null;
      }>,
    ) => {
      const { searchResultsKey, latitude, longitude } = action.payload;
      const uiState = getUiStateOrThrow(state, searchResultsKey);
      if (
        uiState.queryStringFilters.latitude === latitude &&
        uiState.queryStringFilters.longitude === longitude
      ) {
        uiState.filterStateFakeUrl = clearQueryStringLocationFilter(
          uiState.filterStateFakeUrl,
        );
      } else {
        uiState.filterStateFakeUrl = setQueryStringLocationFilter(
          uiState.filterStateFakeUrl,
          String(latitude),
          String(longitude),
        );
      }
      updateQueryStringFilters(state, searchResultsKey);
      updateFilteredSearchResults(state, searchResultsKey);
    },
    setDateReference: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        dateReference: string;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      if (
        uiState.queryStringFilters.dateReference ===
        action.payload.dateReference
      ) {
        uiState.filterStateFakeUrl = clearQueryStringDateReferenceFilter(
          uiState.filterStateFakeUrl,
        );
      } else {
        uiState.filterStateFakeUrl = setQueryStringDateReferenceFilter(
          action.payload.dateReference,
          uiState.filterStateFakeUrl,
        );
      }
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setFeedFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        filterBy: TQueryStringFeedFilter["filterBy"];
        value: TQueryStringFeedFilter["value"];
        dateInUserTz?: string;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringFeedFilter(
        {
          filterBy: action.payload.filterBy,
          value: action.payload.value,
          date: action.payload.dateInUserTz,
        },
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    clearSecondaryFeedFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = clearSecondaryQueryStringFeedFilter(
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    clearFeedFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = clearQueryStringFeedFilter(
        uiState.filterStateFakeUrl,
      );
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setBlurMedia: (
      //We should add this and the translateAll toggle to the queryString
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        blurMedia: boolean;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.blurMedia = action.payload.blurMedia;
    },
    setTranslateAll: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        translateAll: boolean;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.translateAll = action.payload.translateAll;
    },
    setCondenseText: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        condenseText: boolean;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.filterStateFakeUrl = setQueryStringCondenseText(
        action.payload.condenseText,
        uiState.filterStateFakeUrl,
      );
      if (
        !action.payload.condenseText &&
        uiState.queryStringFilters.sort === "Occurrences"
      ) {
        uiState.filterStateFakeUrl = setQueryStringSort(
          null,
          uiState.filterStateFakeUrl,
        );
      }
      updateQueryStringFilters(state, action.payload.searchResultsKey);
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setSearchType: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        searchType: string;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.searchType = action.payload.searchType;
    },
    setGraphMode: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        graphMode: GraphMode;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.graphMode = action.payload.graphMode;
      uiState.graphData = generateGraphData(
        uiState.filteredSearchResults,
        uiState.rankingsData,
        uiState.activeRankingsTab,
        uiState.graphMode,
        uiState.graphType,
        uiState.queryStringFilters.feed
          ? uiState.queryStringFilters.feed.value
          : null,
      );
    },
    setGraphType: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        graphType: GraphType;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.graphType = action.payload.graphType;
      uiState.graphData = generateGraphData(
        uiState.filteredSearchResults,
        uiState.rankingsData,
        uiState.activeRankingsTab,
        uiState.graphMode,
        uiState.graphType,
        uiState.queryStringFilters.feed
          ? uiState.queryStringFilters.feed.value
          : null,
      );
    },
    setActiveRankingsTab: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        activeRankingsTab: TTabNames;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.activeRankingsTab = action.payload.activeRankingsTab;
      uiState.rankingsData = generateRankings(
        uiState.filteredSearchResults,
        action.payload.activeRankingsTab,
      );
      uiState.graphData = generateGraphData(
        uiState.filteredSearchResults,
        uiState.rankingsData,
        uiState.activeRankingsTab,
        uiState.graphMode,
        uiState.graphType,
        uiState.queryStringFilters.feed
          ? uiState.queryStringFilters.feed.value
          : null,
      );
    },
    setOriginalSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        searchResultsHash: string;
        searchResults: SearchResult[];
        textHighlights: TExplorerHighlightText[];
        reportName: string | undefined;
        narratives: Narrative[];
        isLoaded: boolean;
        filterChangesUpdateBrowserUrl: boolean;
        blurMedia: boolean;
        searchType?: string;
      }>,
    ) => {
      const {
        searchResultsKey,
        searchResultsHash,
        searchResults,
        textHighlights,
        reportName,
        narratives,
        isLoaded,
        filterChangesUpdateBrowserUrl,
        blurMedia,
        searchType,
      } = action.payload;

      const filterStateFakeUrl = filterChangesUpdateBrowserUrl
        ? null
        : // tr=2d hardcodes the time range for the Preview search
          `http://fake-url-for-storing-filter-state.com/explorer?projectId=${uuidv4()}&tr=2d`;

      syncFilteredNarratives(action.payload.narratives, filterStateFakeUrl);

      state[searchResultsKey] = initialExplorerUiState(
        searchResults,
        searchResultsHash,
        textHighlights,
        reportName,
        narratives,
        isLoaded,
        filterStateFakeUrl,
        blurMedia,
        searchType,
      );

      updateFilteredSearchResults(state, searchResultsKey);
    },
  },
});

export const {
  setEvaluatedNarratives,
  setPlatformFilter,
  setLanguageFilter,
  setNarrativeFilter,
  setKeywordFilter,
  setRelevanceFilter,
  setFeedFilter,
  setSort,
  setSortDirection,
  clearSecondaryFeedFilter,
  clearFeedFilter,
  setOriginalSearchResults,
  setMediaCategoryFilter,
  setMediaCategoryFilterResults,
  setBlurMedia,
  setTranslateAll,
  setCondenseText,
  setSearchType,
  setLocationFilter,
  setGraphType,
  setGraphMode,
  setActiveRankingsTab,
  setDateReference,
} = explorerSlice.actions;

export const reducer = explorerSlice.reducer;

export const selectTextHighlights = (
  state: ExplorerUIState,
): TExplorerHighlightText[] => {
  return state.keywordFilterHighlight ?? state.textHighlights;
};

export const selectReportName = (state: ExplorerUIState) => state.reportName;

export const selectBlurMedia = (state: ExplorerUIState) => state.blurMedia;

export const selectTranslateAll = (state: ExplorerUIState) =>
  state.translateAll;

export const selectNarratives = (state: ExplorerUIState) => state.narratives;

export const selectPlatformList = (state: ExplorerUIState) => state.platforms;

export const selectEvaluatedNarratives = (state: ExplorerUIState) =>
  state.evaluatedNarratives;

export const selectQueryStringFilters = (state: ExplorerUIState) =>
  state.queryStringFilters;

export const selectSearchType = (state: ExplorerUIState) => state.searchType;

export const selectLocationFilter = (state: ExplorerUIState) => ({
  latitude: state.queryStringFilters.latitude,
  longitude: state.queryStringFilters.longitude,
});

export const selectSummaryViewerState = (state: ExplorerUIState) => ({
  activeRankingsTab: state.activeRankingsTab,
  rankingsData: state.rankingsData,
  graphMode: state.graphMode,
  graphType: state.graphType,
  graphData: state.graphData,
});

export const selectDateReference = (state: ExplorerUIState) =>
  state.queryStringFilters.dateReference;
